import {
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Flex,
  Tooltip,
  Icon,
  Checkbox,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { pipe, concat, compact } from 'remeda';

import { date as commonDate } from '@finley-technologies/common';

import { CreditAgreementMetadataGQLType } from 'types/dashboard-core-gqlTypes.generated';

import CreditAgreementDiff from './CreditAgreementDiff';
import { RemoveCreditAgreementModal } from './RemoveCreditAgreementModal';
import {
  ActiveDiffIcon,
  DeleteIconButton,
  InfoIconButton,
} from 'packages/common-frontend/components/IconButton';
import { displayValueOrEmpty } from 'packages/common-frontend/utils';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

/**
 * NOTE: Permissioned for lender and admin dash CA configuration
 */
const CreditAgreementTableRow: React.FC<{
  creditAgreementDocumentMetadata: CreditAgreementMetadataGQLType;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  addSelection: Function;
  removeSelection: Function;
  selectDiffDisabled: boolean;
  isCommittedFutureRevision: boolean;
  mostRecentCommitId: string | undefined;
  creditAgreementInfoPathGenerator: (id: string) => string;
}> = ({
  creditAgreementDocumentMetadata,
  setIsOpen,
  selectDiffDisabled,
  addSelection,
  removeSelection,
  isCommittedFutureRevision,
  mostRecentCommitId,
  creditAgreementInfoPathGenerator,
}) => {
  const history = useHistory();

  const {
    id,
    version,
    effectiveFrom,
    effectiveTo,
    _note_,
  } = creditAgreementDocumentMetadata;
  return (
    <>
      <Tr>
        <Td textAlign="center">
          <Checkbox
            isDisabled={selectDiffDisabled}
            onChange={(e) => {
              if (!e.target.checked) {
                removeSelection(creditAgreementDocumentMetadata);
              }
              if (e.target.checked) {
                addSelection(creditAgreementDocumentMetadata);
              }
            }}
          />
        </Td>
        <Td>{id}</Td>
        <Td>{version}</Td>
        <Td>{commonDate.epochToLocaleDateString(effectiveFrom)}</Td>
        <Td>
          {id === mostRecentCommitId
            ? '-'
            : commonDate.epochToLocaleDateString(effectiveTo)}
        </Td>
        <Td>{displayValueOrEmpty(_note_)}</Td>
        <Td>
          <Flex>
            <InfoIconButton
              ariaLabel="info"
              label="More details"
              onClick={() => history.push(creditAgreementInfoPathGenerator(id))}
            />
            {isCommittedFutureRevision && (
              <DeleteIconButton
                ariaLabel="remove"
                label="Remove"
                onClick={() => setIsOpen(true)}
              />
            )}
          </Flex>
        </Td>
      </Tr>
    </>
  );
};

const CreditAgreementTable: React.FC<{
  creditFacilityId: string;
  creditAgreementDocumentsMetadata: CreditAgreementMetadataGQLType[];
  creditAgreementInfoPathGenerator: (id: string) => string;
}> = ({
  creditAgreementDocumentsMetadata,
  creditFacilityId,
  creditAgreementInfoPathGenerator,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCADocumentsForDiff, setSelectedCADocumentsForDiff] = useState<
    [
      CreditAgreement.DomainEntity.CreditAgreementMetadata | null,
      CreditAgreement.DomainEntity.CreditAgreementMetadata | null,
    ]
  >([null, null]);

  const addSelectedCaDocumentsForDiff = useCallback(
    (newSelection: CreditAgreement.DomainEntity.CreditAgreementMetadata) => {
      setSelectedCADocumentsForDiff(
        (
          state: [
            CreditAgreement.DomainEntity.CreditAgreementMetadata | null,
            CreditAgreement.DomainEntity.CreditAgreementMetadata | null,
          ],
        ) => {
          const sortedValues = pipe(state, concat([newSelection]), compact);
          const [ca1, ca2] = sortedValues;
          return [ca1, ca2];
        },
      );
    },
    [setSelectedCADocumentsForDiff],
  );

  const removeSelection = useCallback(
    (
      removedSelection: CreditAgreement.DomainEntity.CreditAgreementMetadata,
    ) => {
      setSelectedCADocumentsForDiff(
        (
          state: [
            CreditAgreement.DomainEntity.CreditAgreementMetadata | null,
            CreditAgreement.DomainEntity.CreditAgreementMetadata | null,
          ],
        ) => {
          const removedIndex = _.findIndex(
            state,
            (document) => document?.id === removedSelection.id,
          );
          const newState = [...state];
          if (removedIndex !== -1) {
            newState[removedIndex] = null;
          }
          const [ca1, ca2] = newState;
          return [ca1, ca2];
        },
      );
    },
    [setSelectedCADocumentsForDiff],
  );

  const currentDateTime = commonDate.currentDateTime().toMillis();
  const mostRecentCommitId = _.first(creditAgreementDocumentsMetadata)?.id;
  const committedFutureRevisionId = _.first(
    _.filter(
      creditAgreementDocumentsMetadata,
      ({ effectiveFrom }) => effectiveFrom > currentDateTime,
    ),
  )?.id;

  const twoCaSelected = _.every(
    selectedCADocumentsForDiff,
    (caDocMetadata) => !_.isNil(caDocMetadata),
  );

  const selectedCAIds = _.map(
    selectedCADocumentsForDiff,
    (document) => document?.id,
  );

  return (
    <>
      {selectedCADocumentsForDiff[1] && selectedCADocumentsForDiff[0] && (
        <CreditAgreementDiff
          creditAgreementId1={selectedCADocumentsForDiff[0].id}
          creditAgreementId2={selectedCADocumentsForDiff[1].id}
        />
      )}
      <Table>
        <Thead>
          <Tr>
            <Th textAlign="center" style={{ whiteSpace: 'nowrap' }}>
              Compare
              <Tooltip label="Select 2 credit agreement versions to compare">
                <Icon fontSize="lg" ml="5px">
                  <ActiveDiffIcon />
                </Icon>
              </Tooltip>
            </Th>
            <Th width="sm">Id</Th>
            <Th width="xs">Version</Th>
            <Th width="sm">Effective From</Th>
            <Th width="sm">Effective To</Th>
            <Th width="sm">Note</Th>
            <Th width="xs" />
          </Tr>
        </Thead>
        <Tbody>
          {creditAgreementDocumentsMetadata.map(
            (creditAgreementDocumentMetadata) => {
              return (
                <CreditAgreementTableRow
                  key={creditAgreementDocumentMetadata.id}
                  setIsOpen={setIsOpen}
                  creditAgreementDocumentMetadata={
                    creditAgreementDocumentMetadata
                  }
                  mostRecentCommitId={mostRecentCommitId}
                  isCommittedFutureRevision={
                    creditAgreementDocumentMetadata.id ===
                    committedFutureRevisionId
                  }
                  selectDiffDisabled={
                    twoCaSelected &&
                    !_.includes(
                      selectedCAIds,
                      creditAgreementDocumentMetadata.id,
                    )
                  }
                  addSelection={addSelectedCaDocumentsForDiff}
                  removeSelection={removeSelection}
                  creditAgreementInfoPathGenerator={
                    creditAgreementInfoPathGenerator
                  }
                />
              );
            },
          )}
        </Tbody>
      </Table>

      {committedFutureRevisionId && (
        <RemoveCreditAgreementModal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          committedFutureRevisionId={committedFutureRevisionId}
          creditFacilityId={creditFacilityId}
        />
      )}
    </>
  );
};

export default CreditAgreementTable;
